import { useMemo, useState } from 'react';
import { Check, Plus, Search, X } from 'lucide-react';
import type { Lead } from '../../../lib/leadsApi';
import { useLabels, useCreateLabel } from '../../../hooks/useLabels';
import { useUpdateLeadById } from '../../../hooks/useLeads';
import { Popover } from '../../tasks/linear/Popover';

/**
 * CLE-192 — label add/remove control for the lead detail sidebar.
 *
 * Leads replace their full label set via `updateLead({ labelIds })`, so add and
 * remove both recompute the id list and issue a single update. Mirrors the task
 * label editor's searchable popover + inline create.
 */

const SWATCHES = [
  '#ef4444',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#14b8a6',
  '#3b82f6',
  '#6366f1',
  '#8b5cf6',
  '#ec4899',
];

export function LeadLabelEditor({ lead }: { lead: Lead }) {
  const update = useUpdateLeadById();

  const appliedIds = useMemo(() => lead.labels.map((l) => l.id), [lead.labels]);

  function setLabels(ids: string[]) {
    update.mutate({ id: lead.id, labelIds: ids });
  }

  function remove(id: string) {
    setLabels(appliedIds.filter((x) => x !== id));
  }

  function add(id: string) {
    if (appliedIds.includes(id)) return;
    setLabels([...appliedIds, id]);
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {lead.labels.map((l) => (
        <button
          key={l.id}
          type="button"
          onClick={() => remove(l.id)}
          title={`Elimină ${l.name}`}
          className="group/lbl inline-flex items-center gap-1.5 rounded-full border border-gray-300 px-2 text-[11px] font-medium text-gray-700 transition-colors hover:border-red-500/60 hover:text-red-500"
          style={{ paddingTop: 1, paddingBottom: 1 }}
        >
          <span
            className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
            style={{ backgroundColor: l.color }}
          />
          {l.name}
          <X size={10} className="opacity-0 group-hover/lbl:opacity-100" />
        </button>
      ))}

      <Popover
        width={240}
        align="start"
        trigger={({ toggle, ref }) => (
          <button
            ref={ref}
            type="button"
            onClick={toggle}
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-2 py-0.5 text-[11px] font-medium text-gray-500 hover:border-gray-400 hover:text-gray-700"
          >
            <Plus size={11} /> {lead.labels.length === 0 ? 'Adaugă etichetă' : 'Adaugă'}
          </button>
        )}
      >
        {(close) => (
          <LabelMenu appliedIds={appliedIds} onAdd={add} onRemove={remove} close={close} />
        )}
      </Popover>
    </div>
  );
}

function LabelMenu({
  appliedIds,
  onAdd,
  onRemove,
  close,
}: {
  appliedIds: string[];
  onAdd: (labelId: string) => void;
  onRemove: (labelId: string) => void;
  close: () => void;
}) {
  // Only the lead catalogue — the shared list also holds every task label.
  const { data: labels } = useLabels('leads');
  const createLabel = useCreateLabel('leads');
  const [query, setQuery] = useState('');
  const [color, setColor] = useState(SWATCHES[5]);

  const applied = useMemo(() => new Set(appliedIds), [appliedIds]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = labels ?? [];
    return q ? all.filter((l) => l.name.toLowerCase().includes(q)) : all;
  }, [labels, query]);

  const exactMatch = (labels ?? []).some(
    (l) => l.name.toLowerCase() === query.trim().toLowerCase(),
  );
  const canCreate = query.trim().length > 0 && !exactMatch;

  function create() {
    const name = query.trim();
    if (!name) return;
    createLabel.mutate(
      { name, color },
      {
        onSuccess: (label) => {
          onAdd(label.id);
          setQuery('');
        },
      },
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Search size={14} className="text-gray-400" />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canCreate) create();
          }}
          placeholder="Caută sau creează…"
          className="w-full bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
        />
      </div>

      <div className="max-h-56 overflow-y-auto py-1">
        {filtered.map((l) => {
          const isOn = applied.has(l.id);
          return (
            <button
              key={l.id}
              type="button"
              onClick={() => (isOn ? onRemove(l.id) : onAdd(l.id))}
              className="ui-menu-item justify-between"
            >
              <span className="flex items-center gap-2">
                <span
                  className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: l.color }}
                />
                <span className="truncate">{l.name}</span>
              </span>
              {isOn && <Check size={14} className="text-gray-400" />}
            </button>
          );
        })}
        {filtered.length === 0 && !canCreate && (
          <p className="px-3 py-2 text-sm text-gray-400">Nicio etichetă.</p>
        )}
      </div>

      {canCreate && (
        <div className="border-t border-border px-3 py-2">
          <div className="mb-1.5 flex items-center gap-1">
            {SWATCHES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                className={`h-4 w-4 rounded-full ${color === c ? 'ring-2 ring-offset-1 ring-offset-[var(--surface-white)]' : ''}`}
                style={{ backgroundColor: c }}
                aria-label={`Culoare ${c}`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={create}
            disabled={createLabel.isPending}
            className="flex w-full items-center gap-2 rounded px-1 py-1.5 text-sm text-gray-700 hover:bg-accent disabled:opacity-50"
          >
            <Plus size={14} /> Creează „{query.trim()}”
          </button>
        </div>
      )}

      <div className="border-t border-border py-1">
        <button type="button" onClick={close} className="ui-menu-item text-gray-500">
          Gata
        </button>
      </div>
    </div>
  );
}
